import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import api from '../utils/api';

const sourceLabels = {
  custom: { telugu: 'ప్రత్యేక సమాచారం', sanskrit: 'विशेष सूचना' },
  selected_vatus: { telugu: 'వటువుల వివరాలు', sanskrit: 'वटूनां विवरणम्' },
  featured_photos: { telugu: 'విశిష్టులు', sanskrit: 'विशिष्टाः' },
  top_performers: { telugu: 'అగ్ర ప్రదర్శకులు', sanskrit: 'उत्तम प्रदर्शकाः' },
  exam_batches: { telugu: 'పరీక్షా కార్యక్రమాలు', sanskrit: 'परीक्षा कार्यक्रमाः' },
  antyakshari_batches: { telugu: 'అంత్యాక్షరి కార్యక్రమం', sanskrit: 'अन्त्याक्षरी कार्यक्रमः' }
};

const getTitle = (slide, lang) => slide.title || sourceLabels[slide.type]?.[lang] || '';

const HomepageSlideshow = () => {
  const { lang } = useLanguage();
  const { teluguYearTelugu, yearId } = useTeluguYear();
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadSlides = async () => {
      try {
        const response = await api.get('/api/admin-config/active');
        const result = await response.json();
        const configured = result.success ? result.data?.homepage_slides || [] : [];
        const enabled = configured.filter(slide => slide.enabled !== false).sort((a, b) => a.order - b.order);
        const hydrated = await Promise.all(enabled.map(async (slide) => {
          if (slide.type === 'custom') return { ...slide, items: [] };
          const urls = {
            selected_vatus: `/api/vatu/slideshow/vatus?ids=${encodeURIComponent((slide.selected_vatu_ids || []).join(','))}`,
            featured_photos: `/api/vatu/slideshow/featured-photos?year=${encodeURIComponent(teluguYearTelugu)}`,
            top_performers: `/api/result/top-vatus?year_id=${encodeURIComponent(yearId)}&pariksha_type=krishna_yajurveda&limit=${slide.limit || 5}`,
            exam_batches: `/api/pariksha-batch/year/${encodeURIComponent(teluguYearTelugu)}`,
            antyakshari_batches: `/api/panasala-antyakshari/batches/${encodeURIComponent(teluguYearTelugu)}`
          };
          const dataResponse = await api.get(urls[slide.type]);
          const data = await dataResponse.json();
          return { ...slide, items: data.success ? (data.data || []).slice(0, slide.limit || 5) : [] };
        }));
        if (!cancelled) setSlides(hydrated);
      } catch (error) {
        console.error('Error loading homepage slideshow:', error);
        if (!cancelled) setSlides([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (teluguYearTelugu && yearId) loadSlides();
    return () => { cancelled = true; };
  }, [teluguYearTelugu, yearId]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = window.setInterval(() => setCurrent(index => (index + 1) % slides.length), 2000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const slide = useMemo(() => slides[current], [slides, current]);

  if (loading) {
    return <Paper sx={{ minHeight: { xs: 180, sm: 240, md: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3, background: 'linear-gradient(145deg, #fffdf8 0%, #fff5e8 50%, #f9e3cb 100%)' }}><CircularProgress /></Paper>;
  }
  if (!slide) return null;

  const items = slide.items || [];
  const isVatuPhotoSlide = slide.type === 'selected_vatus';
  return (
    <Paper elevation={3} sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%', minHeight: { xs: 320, sm: 380, md: 500 }, mb: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 3 }, background: 'linear-gradient(145deg, #fffdf8 0%, #fff5e8 50%, #f9e3cb 100%)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2, md: 3 }, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ color: '#7f4b2d', fontWeight: 500, fontSize: { xs: '1.25rem', sm: '1.3rem', md: '1.3rem' } }}>{getTitle(slide, lang)}</Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 }}>
        {slide.image && <Box component="img" src={slide.image} alt="" sx={{ width: '100%', height: { xs: 90, sm: 120, md: 180 }}} />}
        {isVatuPhotoSlide && items.length > 0 && (
          <Box sx={{ width: '100%', boxSizing: 'border-box', minHeight: { xs: 170, sm: 220, md: 280 }, maxHeight: { md: 330 }, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, md: 1.5 }, p: { xs: 1.5, md: 2 }, flexWrap: 'wrap' }}>
            {items.map((item, index) => {
              const photo = item.photo_download_link || (item.vatu_id ? `/api/vatu/download/${item.vatu_id}/photo` : null);
              return photo && (
                <Box key={item._id || item.vatu_id || index} sx={{ width: { xs: 170, sm: 230, md: 280 }, height: { xs: 170, sm: 230, md: 280 }, flex: '0 0 auto', borderRadius: '50%', overflow: 'hidden'}}>
                  <Box component="img" src={photo} alt={item.vatu_name || ''} sx={{ display: 'block', width: '100%', height: '100%' }} />
                </Box>
              );
            })}
          </Box>
        )}
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, textAlign: 'center', minWidth: 0 }}>
        {items.length > 0 && !isVatuPhotoSlide && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mt: 2, flexWrap: 'wrap' }}>
            {items.map((item, index) => {
              const photo = item.photo_download_link || (item.vatu_id ? `/api/vatu/download/${item.vatu_id}/photo` : null);
              return <Box key={item._id || item.vatu_id || index} sx={{ width: { xs: 75, sm: 105, md: 125 } }}>
                {photo && <Box component="img" src={photo} alt={item.vatu_name || ''} sx={{ width: { xs: 58, sm: 82, md: 100 }, height: { xs: 58, sm: 82, md: 100 }, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center', border: '3px solid #7f4b2d' }} />}
                <Typography sx={{ mt: 0.5, color: '#7f4b2d', fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>{item.vatu_name || item.name || item.name_of_batch || item.title || ''}</Typography>
              </Box>;
            })}
          </Box>
        )}
        {slide.link && <Button href={slide.link} sx={{ mt: 2 }} variant="contained">Learn more</Button>}
        {isVatuPhotoSlide && items.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {items.map((item, index) => <Typography key={item._id || item.vatu_id || index} sx={{ color: '#7f4b2d', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>{item.vatu_name || ''}{item.pathasala_name ? ` - ${item.pathasala_name}` : ''}</Typography>)}
          </Box>
        )}
        </Box>
      </Box>
      {slide.description && <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: { xs: 2, md: 3 }, textAlign: 'center' }}><Typography sx={{ color: '#36516f', fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>{slide.description}</Typography></Box>}
      {slides.length > 1 && <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, pb: 1.5 }}>{slides.map((item, index) => <Box key={item.id} sx={{ width: index === current ? 24 : 8, height: 8, borderRadius: 4, backgroundColor: index === current ? '#7f4b2d' : '#9db1c7', transition: 'width 0.2s' }} />)}</Box>}
    </Paper>
  );
};

export default HomepageSlideshow;
